import { Input } from 'components/atoms';
import ImageInputFormField from 'components/atoms/inputs/imageForm/ImageInputForm';
import { FormInput } from 'components/molecules';
import DataWrapper from 'components/molecules/data-wrapper/DataWrapper';
import ViewHint from 'components/molecules/hint/ViewHint';
import IsBandSelectPopupForm from 'components/molecules/popup/is-band-select/IsBandSelectPopupForm';
import StepsTab from 'components/molecules/steps/tab/StepsTab';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { IArtistMutationForm } from 'services/artists/types';
import { STATUS } from 'services/types';
import { getCountriesPhoneNumberPrefixOptionValues } from 'utils/global/countries.utils';

const ArtistData = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const {
    values,
    status,
    setFieldValue,
    initialValues,
    submitCount,
    errors,
  }: IFormikContextType<IArtistMutationForm> = useFormikContext<IArtistMutationForm>();

  const { setForm } = useStoreState();

  const onGoToStep = (step: string) => {
    setForm('artistForm', values);
    navigate(`/artist/${values.id ? `edit/${values.id}` : 'create'}/${step}`);
  };

  return (
    <StepsTab
      nextName={values.isBand ? 'band' : 'external'}
      onNext={() => onGoToStep(values.isBand ? 'band' : 'external')}
      isFirst
    >
      <DataWrapper>
        <ViewHint hintId="artist-profile" />
        <div className="h-[200px] w-[200px]">
          <ImageInputFormField<IArtistMutationForm>
            nameCropped="file"
            nameOriginal="fileOriginal"
            draftKey="artist.profilePicture"
            fullResolutionUrl={values.profilePictureLink}
            previewUrl={values.profilePicturePreviewLink}
            editable={!status?.displayMode}
            onDelete={
              initialValues.profilePictureLink
                ? (action) => {
                    setFieldValue('profilePictureLink', '');
                    setFieldValue('profilePicturePreviewLink', '');
                    setFieldValue('file', null);
                    setFieldValue('fileOriginal', null);
                    action.close();
                  }
                : undefined
            }
          />
        </div>

        <FormInput.Text
          required
          name="nickname"
          label={!values.isBand ? t('input.label.nickname') : t('input.label.bandName')}
          autoFocus
        />
        <IsBandSelectPopupForm
          onSelectIsBand={(isBand, actions) => {
            setFieldValue('isBand', isBand ?? null);
            setFieldValue('artistsInBand', []);
            actions.close();
          }}
          initialIsBand={values.isBand}
          displayMode={status?.displayMode}
          trigger={
            <div className="flex w-full cursor-pointer">
              <Input.Text
                required
                name=""
                error={submitCount > 0 ? errors.isBand : undefined}
                label={t('input.label.type')}
                value={
                  typeof values.isBand === 'boolean'
                    ? t(`is-band.${String(values.isBand)}.name`)
                    : ''
                }
                style={{ pointerEvents: 'none' }}
              />
            </div>
          }
        />

        <div />
        <ViewHint hintId="artist-personal-data" />
        <Input.ComplexWrapper sizes={[30, 70]}>
          <FormInput.Text name="prefix" label={t('input.label.prefix')} />
          <FormInput.Text
            name="firstName"
            label={
              !values.isBand ? t('input.label.firstName') : t('input.label.supervisorFirstName')
            }
          />
        </Input.ComplexWrapper>
        <Input.ComplexWrapper sizes={[70, 30]}>
          <FormInput.Text
            name="lastName"
            label={!values.isBand ? t('input.label.lastName') : t('input.label.supervisorLastName')}
          />
          <FormInput.Text name="suffix" label={t('input.label.suffix')} />
        </Input.ComplexWrapper>

        <div />
        <ViewHint hintId="artist-contact" />
        <FormInput.Text name="email" label="Email" />
        <Input.ComplexWrapper sizes={[40, 60]}>
          <FormInput.Select
            name="phoneNumber.prefix"
            label={t('input.label.phoneNumberCodeShort')}
            placeholder=""
            options={getCountriesPhoneNumberPrefixOptionValues()}
            isCreatable
            onChange={(option) => setFieldValue('phoneNumberPrefix', option.value)}
          />
          <FormInput.Text
            name="phoneNumber.number"
            label={t('input.label.phoneNumber')}
            type="number"
          />
        </Input.ComplexWrapper>
        <div />
        {!!status?.displayMode && <ViewHint hintId={STATUS[values.statusId]} />}
      </DataWrapper>
    </StepsTab>
  );
};

export default ArtistData;
